import { timestampToFomattedDate } from '../../utils/date'
import type { Group, IDMap } from '../../utils/groups'
import { addItemToGroup, getEmptyGroup } from '../../utils/groups'
import { getSessionEventConfig } from '../events/functions'
import type { RawSessionEventData } from '../events/types'
import type { SessionBroadcastIntergrationCompletion } from '../genius/functions'
import type { FormattedHardware } from '../hardware/types'
import type { FormattedHardwareSession } from '../hardware_sessions/types'
import { getMatchSides } from '../match'
import type { RawMatchState } from '../match/types'
import type { RawPersonData } from '../people/types'
import { isDoublePitchSetup } from '../pitches/functions'
import type {
  Anchor,
  Pitch,
  PitchArcs,
  PitchCoordinates,
  PitchTypes,
  RawPitchData
} from '../pitches/types'
import type { SportsConfig } from '../sports/config'
import { EMPTY_SPORT_CONFIG } from '../sports/constants'
import type { SportTypeValues } from '../sports/data_types'
import { sportTypes } from '../sports/data_types'
import { getSport } from '../sports/functions'
import type {
  FormattedTeamPerson,
  RawTeamPersonData
} from '../team_people/types'
import { formatTeamPersonSessions } from '../team_person_sessions/functions'
import { formatTeamSessions } from '../team_sessions/functions'
import { EMPTY_FORMATTED_TEAM } from '../teams'
import { formatTeams } from '../teams/functions'
import type { RawTeamData } from '../teams/types'
import { EMPTY_FORMATTED_SESSION, EMPTY_PROCESSED_SESSION } from './constants'
import type { SessionTypeValues } from './data_types'
import { sessionTypes } from './data_types'
import { sessionModeTypes } from './modes/data_types'
import { sessionStateTypes } from './states/data_types'
import type { SubSessionTypeValues } from './sub_sessions/data_types'
import { subSessionTypes } from './sub_sessions/data_types'
import type {
  FormattedSession,
  ProcessedSessionEvent,
  RawSessionData
} from './types'

export function processSessionEvent(
  sessionEvent: RawSessionEventData,
  sportsConfig: SportsConfig | null,
  activeSessionId: string | null,
  teamsMap: IDMap<RawTeamData>
): ProcessedSessionEvent {
  if (!sessionEvent) return EMPTY_PROCESSED_SESSION

  if (sessionEvent.dataType !== 'Session') return EMPTY_PROCESSED_SESSION

  const session = sessionEvent.data

  if (!session) return EMPTY_PROCESSED_SESSION

  if (!sportsConfig) return EMPTY_PROCESSED_SESSION

  // Event Types //

  const sportConfig =
    sportsConfig.sportTypes[session.sport] || EMPTY_SPORT_CONFIG

  const { sessionSubTypeConfig, sessionTypeConfig } = getSessionEventConfig(
    sportsConfig,
    session.sport,
    session.type,
    session.subType
  )

  const mode = sessionModeTypes.getTypeByValue(session.mode)
  const state = sessionStateTypes.getTypeByValue(session.state)

  const tagValues = Object.values(sessionEvent.tags || {})
    .map((tagArray) => tagArray)
    .flat()

  const tags = tagValues.map((tagValue) => {
    const tagConfig =
      sessionSubTypeConfig.tags[tagValue] ||
      sessionTypeConfig.tags[tagValue] ||
      sportConfig.events.Session.tags[tagValue]

    return (
      tagConfig || {
        name: tagValue,
        value: tagValue,
        color: 'grey',
        description: 'Tag not valid for this session type',
        enabled: false
      }
    )
  })

  let homeTeam: RawTeamData | undefined = undefined
  let awayTeam: RawTeamData | undefined = undefined

  if (session.teamSessions?.length > 0) {
    homeTeam = teamsMap[session.teamSessions[0]?.teamId]
    awayTeam = teamsMap[session.teamSessions[1]?.teamId]
  } else if (session.summary) {
    homeTeam = teamsMap[session.summary.homeTeamId]
    awayTeam = teamsMap[session.summary.awayTeamId]
  }

  const taggedPlayerCount =
    session.players?.filter((p) => p.hardwareId).length ?? 0

  const processedEvent: ProcessedSessionEvent = {
    ...sessionEvent,

    rawData: sessionEvent,

    typeConfig: sessionTypeConfig,
    subTypeConfig: sessionSubTypeConfig,

    mode,

    state,
    isNotStarted: session.state === 'NotStarted',
    isStarted: session.state === 'Started',
    isFinished: session.state === 'Finished',
    isLocal: activeSessionId === sessionEvent.id,

    sport: getSport(session.sport),
    sportConfig,

    homeTeam: homeTeam,
    awayTeam: awayTeam,
    taggedPlayerCount: taggedPlayerCount,
    playerCount: session.players?.length ?? 0,
    ballCount: session.hardware?.length ?? 0,

    startTime: timestampToFomattedDate(sessionEvent.startTime),
    endTime: timestampToFomattedDate(sessionEvent.endTime),

    tags
  }

  return processedEvent
}

export function formatSessionData(
  sessionEvent: RawSessionEventData,
  rawTeams: IDMap<RawTeamData>,
  rawTeamPeople: IDMap<RawTeamPersonData>,
  rawPeople: IDMap<RawPersonData>,
  liveOnDevice: boolean,
  pitchInUse: Pitch | null,
  sportsConfig: SportsConfig
): FormattedSession {
  if (!sessionEvent) return EMPTY_FORMATTED_SESSION

  const { data: session } = sessionEvent

  if (!session) return EMPTY_FORMATTED_SESSION

  try {
    // An opposition team on a shared fixture is not in the caller's own team
    // list, so fall back to the team the session payload carries. flatMap drops
    // a team that resolves to neither instead of leaving a hole in the array.
    const teamsArray = session.teamSessions.flatMap(
      (teamsSession) => rawTeams[teamsSession.teamId] ?? teamsSession.team ?? []
    )

    // A club-narrowed response substitutes a placeholder for each hidden
    // opposition team's players. That placeholder is never in the caller's
    // org-scoped roster, so resolve from the roster first and fall back to the
    // team person the player session carries.
    const teamPeopleArray = session.players.flatMap(
      (playerSession) =>
        rawTeamPeople[playerSession.teamPersonId] ??
        playerSession.teamPerson ??
        []
    )

    const teams = formatTeams(teamsArray, teamPeopleArray, rawPeople)

    // Get sport from session sportType //
    const sport = getSport(session.sport)
    // ================================= //
    const isTrainingMode = isTraining(session.type)
    const isMatchMode = isMatch(session.type)

    // Teams and Players //
    const playersSessions = formatTeamPersonSessions(session.players, teams)

    const teamSessions = formatTeamSessions(
      session.teamSessions,
      teams,
      playersSessions.byId
    )

    const players: {
      [teamId: string]: Group<FormattedTeamPerson>
      All: Group<FormattedTeamPerson>
      byTeamPersonSessionId: Group<FormattedTeamPerson, string>
    } = {
      All: getEmptyGroup<FormattedTeamPerson>(),
      byTeamPersonSessionId: getEmptyGroup<FormattedTeamPerson, string>()
    }

    playersSessions.byId.list.forEach((teamPersonSession) => {
      if (!teamPersonSession.teamPerson) return
      addItemToGroup(
        players.byTeamPersonSessionId,
        teamPersonSession.teamPerson,
        teamPersonSession.id,
        'id',
        teamPersonSession.teamPerson.fullName
      )
    })

    teams.list.forEach((team) => {
      players[team.id] = getEmptyGroup<FormattedTeamPerson>()
      team.teamPeople.list.forEach((teamPerson) => {
        addItemToGroup(
          players.All,
          teamPerson,
          teamPerson.id,
          'id',
          teamPerson.fullName
        )
        addItemToGroup(
          players[team.id],
          teamPerson,
          teamPerson.id,
          'id',
          teamPerson.fullName
        )
      })
    })

    // Balls //
    const balls = getEmptyGroup<FormattedHardwareSession>()

    if (session.hardware) {
      session.hardware.forEach((hardware) => {
        if (hardware.deviceType === 'Ball') {
          const formattedHardware: FormattedHardwareSession = {
            ...hardware
          }
          addItemToGroup(
            balls,
            formattedHardware,
            formattedHardware.hardwareId,
            'hardwareId',
            formattedHardware.hardwareId
          )
        }
      })
    }

    // ================================= //

    // Get home and away team ids //
    let homeTeamId = '',
      awayTeamId = ''
    teamSessions.list.forEach((teamSession) => {
      if (teamSession.homeTeam) {
        homeTeamId = teamSession.teamId
      } else {
        awayTeamId = teamSession.teamId
      }
    })
    // ================================= //

    // Event Types //

    const { sessionSubTypeConfig, sessionTypeConfig } = getSessionEventConfig(
      sportsConfig,
      session.sport,
      session.type,
      session.subType
    )

    // Metrics //
    const playerSummaryMetrics =
      sportsConfig.sportTypes?.[session.sport]?.playerSummaryMetrics ??
      sportsConfig.playerSummaryMetrics

    // ================================= //

    // Pitch and anchors //
    const { pitch, anchorConfig, rawPitch } = getStrackPitchAndAnchorConfig(
      session.pitch || pitchInUse,
      session.sport
    )
    const anchors = getEmptyGroup<Anchor, number>()
    for (const anchorId in anchorConfig) {
      const anchor = anchorConfig[anchorId]
      addItemToGroup(anchors, anchor, String(anchor.id), 'id', anchor.serial)
    }
    // ================================= //

    const returnData = {
      sessionData: session,
      sessionEvent,

      isSetup: !session.id,

      id: session.id || 'setup',
      name: session.name,
      startTime: timestampToFomattedDate(sessionEvent.startTime),
      endTime: timestampToFomattedDate(sessionEvent.endTime),
      sport,
      type: sessionTypes.getTypeByValue(session.type),
      subType: subSessionTypes.getTypeByValue(session.subType),
      mode: sessionModeTypes.getTypeByValue(session.mode),
      state: sessionStateTypes.getTypeByValue(session.state),
      isTrainingMode,
      isMatchMode,
      liveOnDevice: liveOnDevice,
      live: session.state === 'Started',
      hostname: session.hostName,
      locationName: session.locationName,

      // Teams and Players
      teams,
      teamSessions,
      players: players,
      playersSessions,
      homeTeam: teams.map[homeTeamId] || EMPTY_FORMATTED_TEAM,
      awayTeam: teams.map[awayTeamId] || EMPTY_FORMATTED_TEAM,

      // Player Metrics
      playerSummaryMetrics,

      // Balls
      balls: balls,

      // Event types and metrics
      sessionEventConfig: sessionSubTypeConfig,

      // Pitch and anchors
      pitchId: session.pitchId,
      pitch,
      rawPitch,
      anchorConfig,
      anchors,

      // Features
      homeSideCheck: sport.props.features.homeSideCheck && isMatchMode
    }

    return returnData
  } catch (e) {
    console.error(e)

    return EMPTY_FORMATTED_SESSION
  }
}

// Pitch and anchor
export const getStrackPitchAndAnchorConfig = (
  pitch: PitchTypes | RawPitchData | Pitch | null,
  sportType?: SportTypeValues
) => {
  const config: {
    anchorConfig: Record<string, Anchor>
    pitch: {
      sport: SportTypeValues | null
      coordinates: PitchCoordinates | null
      arcs: PitchArcs | null
    }
    rawPitch: PitchTypes | RawPitchData | Pitch | null
  } = {
    anchorConfig: {},
    pitch: {
      sport: sportType || pitch?.sport || null,
      coordinates: null,
      arcs: null
    },
    rawPitch: pitch
  }

  if (pitch && pitch.pitch) {
    if (
      sportType &&
      pitch.sport !== sportType &&
      pitch.sport !== sportTypes.items.demonstration.value
    ) {
      config.pitch = {
        sport: sportType,
        coordinates: null,
        arcs: null
      }
      config.anchorConfig = {}
    } else {
      config.pitch = {
        sport: pitch.sport,
        // Runtime stores the pitch dimensions object here; the coordinates
        // point-map is read off it downstream.
        // TODO: revisit cast — align the runtime shape with the type.
        coordinates: pitch.pitch as unknown as PitchCoordinates,
        arcs: pitch.arcs
      }
      if (pitch.anchors) {
        for (const key in pitch.anchors) {
          config.anchorConfig[key] = pitch.anchors[key]
        }
      }
    }
  }

  return config
}

export function isMatch(sessionTypeValue: SessionTypeValues) {
  return sessionTypeValue === sessionTypes.items.match.value
}

export function isTraining(sessionTypeValue: SessionTypeValues) {
  return sessionTypeValue === sessionTypes.items.training.value
}

export const getSessionTeamSides = (
  matchState: RawMatchState,
  formattedSession: FormattedSession
) => {
  if (!formattedSession) {
    return []
  }

  if (matchState && matchState.session.id === formattedSession.id) {
    return getMatchSides(matchState)
  } else {
    return formattedSession.teams.list.map((team) => team.rawData)
  }
}

// Session Setup Status //
export const getSessionSetupStatus = (
  formattedSession: FormattedSession,
  formattedHardware: FormattedHardware,
  sessionBroadcastIntegrationCompletion: SessionBroadcastIntergrationCompletion
) => {
  const status: Record<
    'broadcastIntegration' | 'playersSessions' | 'offlineBalls',
    { type: string; message: string | null }
  > = {
    broadcastIntegration: {
      type: 'error',
      message: null
    },
    playersSessions: {
      type: 'error',
      message: null
    },
    offlineBalls: {
      type: 'warning',
      message: null
    }
  }

  // Remove devices that are not included in formatted hardware (not being sent by the server)
  const playersSessionsWithHardwareList =
    formattedSession.playersSessions.byHardwareId.list.filter(
      (playerSession) => {
        return playerSession.hardwareId
          ? formattedHardware.devices.map[playerSession.hardwareId]
          : undefined
      }
    )

  // Check if broadcast integration is complete
  if (!sessionBroadcastIntegrationCompletion.isComplete) {
    status.broadcastIntegration.message =
      sessionBroadcastIntegrationCompletion.errors[0]
  }

  // Check if offline balls are in the session //
  playersSessionsWithHardwareList.forEach((playerSession) => {
    const device = playerSession.hardwareId
      ? formattedHardware.types.ball.status.offline.devices.map[
          playerSession.hardwareId
        ]
      : undefined
    if (device)
      status.offlineBalls.message =
        'Offline balls were added to the session. Are you sure you want to start a session?'
  })

  return status
}

// Session Assignment Status //
// Check if players tags have been assigned to the session
export const hasPlayerTagsAssigned = (
  formattedSession: FormattedSession,
  formattedHarware: FormattedHardware
) => {
  return (
    formattedSession?.playersSessions.byPlayerId.list.some((playerSession) => {
      return playerSession.hardwareId
        ? formattedHarware.devices.map[playerSession.hardwareId]
        : undefined
    }) || false
  )
}

// Counts each team's on-field player tags for the current session. A tag counts
// when it is a PlayerTag (resolved via the PlayerTag-scoped device group) and is
// within the pitch bounds (inPitch) — the sole on-field signal. Team comes from
// the player-session record's teamId; hardwareId only bridges to the device.
export function getOnFieldPlayerCounts(
  formattedSession: FormattedSession,
  formattedHardware: FormattedHardware
): { [teamId: string]: number } {
  const counts: { [teamId: string]: number } = {}

  formattedSession.playersSessions.byId.list.forEach((playerSession) => {
    if (!playerSession.hardwareId) return

    const device =
      formattedHardware.types.playerTag.devices.map[playerSession.hardwareId]
    if (!device) return

    if (device.metrics.inPitch.value)
      counts[playerSession.teamId] = (counts[playerSession.teamId] ?? 0) + 1
  })

  return counts
}

// Check if players have been assigned to the session
export const hasPlayersAssigned = (formattedSession: FormattedSession) => {
  return formattedSession?.playersSessions.byPlayerId.count > 0
}

// Check if online balls have been assigned to the session
export const hasOnlineBallsAssigned = (
  formattedSession: FormattedSession,
  formattedHarware: FormattedHardware
) => {
  return (
    formattedSession?.balls.list.some((playerSession) => {
      return (
        formattedHarware.status.online.devices.map[playerSession.hardwareId] ||
        formattedHarware.status.sleep.devices.map[playerSession.hardwareId]
      )
    }) || false
  )
}

// Check if offline balls have been assigned to the session
export const hasOfflineBallsAssigned = (
  formattedSession: FormattedSession,
  formattedHarware: FormattedHardware
) => {
  return (
    formattedSession?.balls.list.some((playerSession) => {
      return formattedHarware.status.offline.devices.map[
        playerSession.hardwareId
      ]
    }) || false
  )
}

// Check if pitch setup is complete
export const pitchSetupComplete = (
  formattedSession: FormattedSession,
  doublePitchSetup: boolean
): boolean => {
  if (!formattedSession) return false

  const isSessionPitchDoublePitchSetup = isDoublePitchSetup(
    formattedSession.pitch.sport,
    formattedSession.pitch.coordinates
  )

  if (doublePitchSetup !== isSessionPitchDoublePitchSetup) return false

  return (
    !!formattedSession.pitch.coordinates && formattedSession.anchors.count > 1
  )
}
// ================================= //

// Get blank session based on permissions //
export const getBlankSession = (
  appSetup: {
    sportType: SportTypeValues
    sessionType: SessionTypeValues
    sessionSubType: SubSessionTypeValues
  },
  perms?: unknown
): RawSessionData => {
  return {
    id: null,
    name: '',

    pitch: null,
    sport: appSetup.sportType,
    type: appSetup.sessionType,
    subType: appSetup.sessionSubType,
    mode: sessionModeTypes.items.live.value,
    state: null,
    pitchId: '0',
    locationName: null,
    hostName: null,
    host: null,
    match: null,
    matchId: null,
    config: {},
    attributes: {},

    players: [],
    teamSessions: [],
    hardware: [],

    eventId: null
    // Blank sentinel for a not-yet-created session; null fields are
    // populated once the session is created on the server.
    // TODO: revisit cast — honest typing needs the shared type widened.
  } as unknown as RawSessionData
}

// Get blank session event//
export const getBlankSessionEvent = (
  data?: RawSessionData
): RawSessionEventData => {
  return {
    children: [],
    createdAt: null,
    updatedAt: null,
    ignore: false,
    startTime: '',
    hierarchy_level: 'Session',
    dataType: 'Session',
    id: null,
    parentId: null,
    endTime: null,
    tags: {},
    properties: {},
    outcomes: [],

    data: data || {
      id: null,
      name: '',

      pitch: null,
      type: sessionTypes.items.training.value,
      subType: subSessionTypes.items.teamPractice.value,
      mode: sessionModeTypes.items.live.value,
      sport: 'Soccer',
      state: null,
      pitchId: '0',
      locationName: null,
      hostName: null,
      host: null,
      match: null,
      matchId: null,
      config: {},
      attributes: {},

      players: [],
      teamSessions: [],
      hardware: [],

      eventId: null
    }
    // Blank sentinel session event; null fields are populated on creation.
    // TODO: revisit cast.
  } as unknown as RawSessionEventData
}

// Generate a random number from a range that is not already contained in a set of numbers
// initialNumber is a seed, not a guarantee: a roster row can carry no jersey
// number, which the typeof guard below already rolls a random number for.
export const generateRandomNumberNotInList = (
  list: number[],
  max: number,
  initialNumber: number | null
) => {
  // If list is full return null
  if (list.length === max) return null

  let number = initialNumber
  let isValidNumber = false
  while (!isValidNumber) {
    if (typeof number === 'number' && !list.includes(number)) {
      isValidNumber = true
    }
    if (!isValidNumber) {
      number = Math.floor(Math.random() * max)
    }
  }

  return number
}

// Get the lowest available number greater than 0 from an array of numbers
export const getFirstAvailableNumber = (list: number[]) => {
  let number = 1
  while (list.includes(number)) {
    number++
  }

  return number
}

// Get formatted session from state //
export function getFormattedSession(
  session: RawSessionEventData,
  {
    teamsMap,
    teamPeopleMap,
    peopleMap,
    pitchInUse,
    activeSessionId,
    sportsConfig
  }: {
    teamsMap: IDMap<RawTeamData>
    teamPeopleMap: IDMap<RawTeamPersonData>
    peopleMap: IDMap<RawPersonData>
    pitchInUse: Pitch | null
    // null when no session is active on this device; the setup/blank session's
    // own id is also null, so null === null keeps a setup session live-on-device.
    activeSessionId: string | null
    sportsConfig: SportsConfig
  }
) {
  const liveOnDevice = activeSessionId === session?.id

  return formatSessionData(
    session,
    teamsMap,
    teamPeopleMap,
    peopleMap,
    liveOnDevice,
    pitchInUse,
    sportsConfig
  )
}
